import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  MenuItem,
  Menu,
  IconButton,
  Divider,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Map as MapIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  DateRange as DateRangeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Folder as FolderIcon,
  Hotel as HotelIcon,
  LocalActivity as ActivityIcon,
  DirectionsCar as TransportIcon,
  FlightTakeoff as FlightIcon,
  MoreVert as MoreVertIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';
import { collection, query, where, getDocs, orderBy, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase/config';
import { format } from 'date-fns';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { v4 as uuidv4 } from 'uuid';

// Define interfaces
interface Trip {
  id: string;
  title: string;
  description: string;
  destination: string;
  startDate: Date;
  endDate: Date;
  coverPhotoUrl: string;
  createdAt: Date;
  updatedAt: Date;
  organizationId: string;
  createdBy: string;
  accommodations: Accommodation[];
  locations: Location[];
  photos: Photo[];
}

interface Accommodation {
  id: string;
  name: string;
  address: string;
  checkIn: Date;
  checkOut: Date;
  price?: number;
  notes?: string;
  photoUrl?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

interface Location {
  id: string;
  name: string;
  address: string;
  date?: Date;
  notes?: string;
  photoUrl?: string;
  category: 'attraction' | 'restaurant' | 'transportation' | 'activity' | 'other';
  coordinates?: {
    lat: number;
    lng: number;
  };
}

interface Photo {
  id: string;
  url: string;
  caption?: string;
  locationId?: string;
  uploadedAt: Date;
}

interface FilterOptions {
  status: 'all' | 'upcoming' | 'past';
  search: string;
}

const OrganizationTripsPage: React.FC = () => {
  const { id: organizationId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { organization } = useOrganization();
  
  // State variables
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    status: 'all',
    search: '',
  });
  const [tabValue, setTabValue] = useState(0);
  const [createTripDialogOpen, setCreateTripDialogOpen] = useState(false);
  const [deleteTripDialogOpen, setDeleteTripDialogOpen] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    destination: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
    coverPhoto: null as File | null,
  });
  const [formErrors, setFormErrors] = useState({
    title: false,
    destination: false,
    startDate: false,
    endDate: false,
  });
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedTripForMenu, setSelectedTripForMenu] = useState<string | null>(null);
  const [accommodations, setAccommodations] = useState<Accommodation[]>([]);
  const [accommodationFormOpen, setAccommodationFormOpen] = useState(false);
  const [currentAccommodation, setCurrentAccommodation] = useState<{
    id?: string;
    name: string;
    address: string;
    checkIn: Date | null;
    checkOut: Date | null;
    price: string;
    notes: string;
  }>({
    name: '',
    address: '',
    checkIn: null,
    checkOut: null,
    price: '',
    notes: '',
  });
  const [accommodationErrors, setAccommodationErrors] = useState({
    name: false,
    address: false,
    checkIn: false,
    checkOut: false,
  });

  // Fetch trips from Firebase
  useEffect(() => {
    const fetchTrips = async () => {
      if (!organizationId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const tripsCollection = collection(db, 'trips');
        const tripsQuery = query(
          tripsCollection,
          where('organizationId', '==', organizationId),
          orderBy('startDate', 'desc')
        );
        
        const tripSnapshot = await getDocs(tripsQuery);
        const tripsList: Trip[] = [];
        
        tripSnapshot.forEach((doc) => {
          const data = doc.data();
          tripsList.push({
            id: doc.id,
            title: data.title,
            description: data.description || '',
            destination: data.destination,
            startDate: data.startDate.toDate(),
            endDate: data.endDate.toDate(),
            coverPhotoUrl: data.coverPhotoUrl || '',
            createdAt: data.createdAt.toDate(),
            updatedAt: data.updatedAt.toDate(),
            organizationId: data.organizationId,
            createdBy: data.createdBy,
            accommodations: data.accommodations || [],
            locations: data.locations || [],
            photos: data.photos || [],
          });
        });
        
        setTrips(tripsList);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching trips:', err);
        setError('Failed to load trips. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchTrips();
  }, [organizationId]);

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Handle filter change
  const handleFilterChange = (filterKey: keyof FilterOptions, value: any) => {
    setFilterOptions({
      ...filterOptions,
      [filterKey]: value,
    });
  };

  // Filter trips based on current filter options
  const filteredTrips = trips.filter((trip) => {
    const matchesSearch = trip.title.toLowerCase().includes(filterOptions.search.toLowerCase()) || 
                         trip.destination.toLowerCase().includes(filterOptions.search.toLowerCase());
    
    const today = new Date();
    const isUpcoming = trip.startDate > today;
    const isPast = trip.endDate < today;
    
    let matchesStatus = true;
    if (filterOptions.status === 'upcoming') {
      matchesStatus = isUpcoming;
    } else if (filterOptions.status === 'past') {
      matchesStatus = isPast;
    }
    
    return matchesSearch && matchesStatus;
  });

  // Handle form input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle date change
  const handleDateChange = (name: 'startDate' | 'endDate', date: Date | null) => {
    setFormData({
      ...formData,
      [name]: date,
    });
  };

  // Handle file input change for cover photo
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData({
        ...formData,
        coverPhoto: e.target.files[0],
      });
    }
  };

  // Handle dialog open/close
  const handleCreateDialogOpen = () => {
    setFormData({
      title: '',
      description: '',
      destination: '',
      startDate: null,
      endDate: null,
      coverPhoto: null,
    });
    setFormErrors({
      title: false,
      destination: false,
      startDate: false,
      endDate: false,
    });
    setAccommodations([]);
    setSelectedTrip(null);
    setCreateTripDialogOpen(true);
  };

  const handleDialogClose = () => {
    setCreateTripDialogOpen(false);
    setDeleteTripDialogOpen(false);
  };

  // Handle edit trip
  const handleEditTrip = (trip: Trip) => {
    setSelectedTrip(trip);
    setFormData({
      title: trip.title,
      description: trip.description,
      destination: trip.destination,
      startDate: trip.startDate,
      endDate: trip.endDate,
      coverPhoto: null,
    });
    setAccommodations(trip.accommodations || []);
    setCreateTripDialogOpen(true);
  };

  // Handle delete trip
  const handleDeleteTrip = async () => {
     if (!selectedTrip) return;

     try {
        // Use backend API to delete trip
        const { createApiUrl } = await import('../../../utils/apiConfig');
        const { getAuthHeaders } = await import('../../../utils/auth');
        const apiUrl = createApiUrl(
           `/api/trips/${selectedTrip.id}?userId=${selectedTrip.createdBy || 'current-user-id'}`
        );
        const headers = await getAuthHeaders();

        const response = await fetch(apiUrl, {
           method: 'DELETE',
           headers,
        });

        const result = await response.json();

        if (!response.ok) {
           throw new Error(result.error || 'Failed to delete trip');
        }

        // Update local state
        setTrips(trips.filter((trip) => trip.id !== selectedTrip.id));
        setDeleteTripDialogOpen(false);
        setSelectedTrip(null);
     } catch (err) {
        console.error('Error deleting trip:', err);
        setError('Failed to delete trip. Please try again.');
     }
  };

  // Handle menu open
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, tripId: string) => {
     setMenuAnchorEl(event.currentTarget);
     setSelectedTripForMenu(tripId);
  };

  // Handle menu close
  const handleMenuClose = () => {
     setMenuAnchorEl(null);
     setSelectedTripForMenu(null);
  };

  // Handle menu actions
  const handleMenuAction = (action: 'view' | 'edit' | 'delete') => {
     if (!selectedTripForMenu) return;

     const trip = trips.find((t) => t.id === selectedTripForMenu);
     if (!trip) return;

     if (action === 'view') {
        navigate(`/organizations/${organizationId}/trips/${trip.id}`);
     } else if (action === 'edit') {
        handleEditTrip(trip);
     } else if (action === 'delete') {
        setSelectedTrip(trip);
        setDeleteTripDialogOpen(true);
     }

     handleMenuClose();
  };

  // Validate form
  const validateForm = () => {
     const errors = {
        title: !formData.title.trim(),
        destination: !formData.destination.trim(),
        startDate: !formData.startDate,
        endDate: !formData.endDate,
     };

     setFormErrors(errors);

     return !Object.values(errors).some((isError) => isError);
  };

  // Handle form submission
  const handleSubmit = async () => {
     if (!validateForm()) return;
     if (!organizationId) return;

     let coverPhotoUrl = selectedTrip?.coverPhotoUrl || '';

     try {
        setLoading(true);

        // Upload cover photo if provided
        if (formData.coverPhoto) {
           const storageRef = ref(
              storage,
              `trips/${organizationId}/${Date.now()}_${formData.coverPhoto.name}`
           );
           const uploadResult = await uploadBytes(storageRef, formData.coverPhoto);
           coverPhotoUrl = await getDownloadURL(uploadResult.ref);
        }

        const tripData = {
           title: formData.title.trim(),
           description: formData.description.trim(),
           destination: formData.destination.trim(),
           startDate: Timestamp.fromDate(formData.startDate!),
           endDate: Timestamp.fromDate(formData.endDate!),
           coverPhotoUrl,
           organizationId,
           createdBy: 'current-user-id', // Replace with actual user ID
           updatedAt: Timestamp.now(),
           accommodations: accommodations.map((acc) => ({
              ...acc,
              checkIn: Timestamp.fromDate(acc.checkIn),
              checkOut: Timestamp.fromDate(acc.checkOut),
           })),
        };

        if (selectedTrip) {
           // Update existing trip
           await updateDoc(doc(db, 'trips', selectedTrip.id), {
              ...tripData,
           });

           // Update local state
           setTrips(
              trips.map((trip) =>
                 trip.id === selectedTrip.id
                    ? {
                         ...trip,
                         ...{
                            ...tripData,
                            // Convert Timestamp back to Date for local state
                            startDate: formData.startDate!,
                            endDate: formData.endDate!,
                            updatedAt: new Date(),
                            accommodations: tripData.accommodations.map((acc) => ({
                               ...acc,
                               // Convert Timestamp back to Date for local state
                               checkIn: acc.checkIn.toDate(),
                               checkOut: acc.checkOut.toDate(),
                            })),
                         },
                      }
                    : trip
              )
           );
        } else {
           // Create new trip using backend API
           const { createApiUrl } = await import('../../../utils/apiConfig');
           const { getAuthHeaders } = await import('../../../utils/auth');
           const apiUrl = createApiUrl('/api/trips');
           const headers = await getAuthHeaders();

           const response = await fetch(apiUrl, {
              method: 'POST',
              headers,
              body: JSON.stringify({
                 title: formData.title.trim(),
                 description: formData.description.trim(),
                 startDate: formData.startDate!.toISOString(),
                 endDate: formData.endDate!.toISOString(),
                 isPublic: false,
                 primaryDestination: formData.destination.trim(),
                 coverPhotoUrl,
                 organizationId,
                 userId: 'current-user-id', // Replace with actual user ID
              }),
           });

           const result = await response.json();

           if (!response.ok) {
              throw new Error(result.error || 'Failed to create trip');
           }

           // Add to local state
           setTrips([
              {
                 id: result.tripId,
                 title: formData.title.trim(),
                 description: formData.description.trim(),
                 destination: formData.destination.trim(),
                 startDate: formData.startDate!,
                 endDate: formData.endDate!,
                 coverPhotoUrl,
                 organizationId,
                 createdBy: 'current-user-id', // Replace with actual user ID
                 createdAt: new Date(),
                 updatedAt: new Date(),
                 accommodations: accommodations.map((acc) => ({
                    ...acc,
                    checkIn: acc.checkIn,
                    checkOut: acc.checkOut,
                 })),
                 locations: [],
                 photos: [],
              },
              ...trips,
           ]);
        }

        setCreateTripDialogOpen(false);
        setLoading(false);
     } catch (err) {
        console.error('Error saving trip:', err);
        setError('Failed to save trip. Please try again.');
        setLoading(false);
     }
  };

  // Get appropriate tab content
  const getTabContent = () => {
    switch (tabValue) {
      case 0: // All Trips
        return renderTripsList(filteredTrips);
      case 1: // Upcoming Trips
        return renderTripsList(trips.filter(trip => trip.startDate > new Date()));
      case 2: // Past Trips
        return renderTripsList(trips.filter(trip => trip.endDate < new Date()));
      default:
        return renderTripsList(filteredTrips);
    }
  };

  // Render trip card
  const renderTripCard = (trip: Trip) => {
    const isUpcoming = trip.startDate > new Date();
    
    return (
      <Card key={trip.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardMedia
          component="img"
          height="140"
          image={trip.coverPhotoUrl || 'https://source.unsplash.com/random?travel'}
          alt={trip.title}
        />
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography gutterBottom variant="h6" component="div" noWrap>
              {trip.title}
            </Typography>
            <IconButton 
              size="small" 
              onClick={(e) => handleMenuOpen(e, trip.id)}
              aria-label="more options"
            >
              <MoreVertIcon />
            </IconButton>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LocationIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {trip.destination}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <DateRangeIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {format(trip.startDate, 'MMM d')} - {format(trip.endDate, 'MMM d, yyyy')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <LocationIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {trip.locations.length} {trip.locations.length === 1 ? 'Location' : 'Locations'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HotelIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {trip.accommodations.length} {trip.accommodations.length === 1 ? 'Accommodation' : 'Accommodations'}
            </Typography>
          </Box>
        </CardContent>
        <CardActions>
          <Button 
            size="small" 
            onClick={() => navigate(`/organizations/${organizationId}/trips/${trip.id}`)}
          >
            View Details
          </Button>
          <Chip 
            label={isUpcoming ? 'Upcoming' : 'Past'} 
            size="small" 
            color={isUpcoming ? 'primary' : 'default'} 
            sx={{ ml: 'auto' }} 
          />
        </CardActions>
      </Card>
    );
  };

  // Render trips list
  const renderTripsList = (tripsToRender: Trip[]) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    
    if (error) {
      return (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      );
    }
    
    if (tripsToRender.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No trips found
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={handleCreateDialogOpen}
          >
            Plan New Trip
          </Button>
        </Box>
      );
    }
    
    return (
      <Grid container spacing={3}>
        {tripsToRender.map((trip) => (
          <Grid item key={trip.id} xs={12} sm={6} md={4} lg={3}>
            {renderTripCard(trip)}
          </Grid>
        ))}
      </Grid>
    );
  };

  // Open accommodation form dialog
  const handleAddAccommodation = () => {
    setCurrentAccommodation({
      name: '',
      address: '',
      checkIn: formData.startDate,
      checkOut: formData.endDate,
      price: '',
      notes: '',
    });
    setAccommodationErrors({
      name: false,
      address: false,
      checkIn: false,
      checkOut: false,
    });
    setAccommodationFormOpen(true);
  };

  // Handle edit accommodation
  const handleEditAccommodation = (accommodation: Accommodation, index: number) => {
    setCurrentAccommodation({
      id: accommodation.id,
      name: accommodation.name,
      address: accommodation.address,
      checkIn: accommodation.checkIn,
      checkOut: accommodation.checkOut,
      price: accommodation.price ? accommodation.price.toString() : '',
      notes: accommodation.notes || '',
    });
    setAccommodationErrors({
      name: false,
      address: false,
      checkIn: false,
      checkOut: false,
    });
    setAccommodationFormOpen(true);
  };

  // Handle remove accommodation
  const handleRemoveAccommodation = (index: number) => {
    const updatedAccommodations = [...accommodations];
    updatedAccommodations.splice(index, 1);
    setAccommodations(updatedAccommodations);
  };

  // Handle accommodation form input change
  const handleAccommodationInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCurrentAccommodation({
      ...currentAccommodation,
      [name]: value,
    });
  };

  // Handle accommodation date change
  const handleAccommodationDateChange = (name: 'checkIn' | 'checkOut', date: Date | null) => {
    setCurrentAccommodation({
      ...currentAccommodation,
      [name]: date,
    });
  };

  // Validate accommodation form
  const validateAccommodationForm = () => {
    const errors = {
      name: !currentAccommodation.name.trim(),
      address: !currentAccommodation.address.trim(),
      checkIn: !currentAccommodation.checkIn,
      checkOut: !currentAccommodation.checkOut,
    };
    
    setAccommodationErrors(errors);
    
    return !Object.values(errors).some(isError => isError);
  };

  // Save accommodation
  const handleSaveAccommodation = () => {
    if (!validateAccommodationForm()) return;
    
    const newAccommodation: Accommodation = {
      id: currentAccommodation.id || uuidv4(),
      name: currentAccommodation.name.trim(),
      address: currentAccommodation.address.trim(),
      checkIn: currentAccommodation.checkIn!,
      checkOut: currentAccommodation.checkOut!,
      notes: currentAccommodation.notes.trim(),
    };

    if (currentAccommodation.price) {
      newAccommodation.price = parseFloat(currentAccommodation.price);
    }
    
    // Check if editing existing accommodation or adding new one
    if (currentAccommodation.id) {
      // Update existing accommodation
      const updatedAccommodations = accommodations.map(acc => 
        acc.id === currentAccommodation.id ? newAccommodation : acc
      );
      setAccommodations(updatedAccommodations);
    } else {
      // Add new accommodation
      setAccommodations([...accommodations, newAccommodation]);
    }
    
    setAccommodationFormOpen(false);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {organization?.name} Trips
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={handleCreateDialogOpen}
        >
          Plan New Trip
        </Button>
      </Box>
      
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="trip status tabs"
          >
            <Tab label="All Trips" />
            <Tab label="Upcoming" />
            <Tab label="Past" />
          </Tabs>
        </Box>
        
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="Search trips..."
                variant="outlined"
                size="small"
                value={filterOptions.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Paper>
      
      <Box sx={{ mt: 2 }}>
        {getTabContent()}
      </Box>
      
      {/* Trip Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleMenuAction('view')}>
          <ListItemIcon>
            <MapIcon fontSize="small" />
          </ListItemIcon>
          View Trip
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('edit')}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          Edit Trip
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('delete')}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <Typography color="error">Delete Trip</Typography>
        </MenuItem>
      </Menu>
      
      {/* Create/Edit Trip Dialog */}
      <Dialog 
        open={createTripDialogOpen} 
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedTrip ? 'Edit Trip' : 'Plan New Trip'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="title"
                label="Trip Title"
                fullWidth
                required
                value={formData.title}
                onChange={handleInputChange}
                error={formErrors.title}
                helperText={formErrors.title ? 'Title is required' : ''}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="destination"
                label="Destination"
                fullWidth
                required
                value={formData.destination}
                onChange={handleInputChange}
                error={formErrors.destination}
                helperText={formErrors.destination ? 'Destination is required' : ''}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Trip Description"
                fullWidth
                multiline
                rows={4}
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Add details about your trip..."
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate}
                  onChange={(date) => handleDateChange('startDate', date)}
                 
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth required error={formErrors.startDate} helperText={formErrors.startDate ? 'Start date is required' : ''} />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={(date) => handleDateChange('endDate', date)}
                 
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth required error={formErrors.endDate} helperText={formErrors.endDate ? 'End date is required' : ''} />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<ImageIcon />}
                fullWidth
              >
                {formData.coverPhoto 
                  ? `Selected: ${formData.coverPhoto.name}` 
                  : selectedTrip?.coverPhotoUrl 
                    ? 'Change Cover Photo' 
                    : 'Upload Cover Photo'}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
            </Grid>

            {/* Accommodations Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Accommodations
                </Typography>
                <Button 
                  variant="outlined" 
                  startIcon={<HotelIcon />} 
                  onClick={handleAddAccommodation}
                >
                  Add Accommodation
                </Button>
              </Box>
              
              {accommodations.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No accommodations added yet. Click the button above to add your first accommodation.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {accommodations.map((accommodation, index) => (
                    <Grid item xs={12} key={accommodation.id}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                              <Typography variant="h6">{accommodation.name}</Typography>
                              <Typography variant="body2" color="text.secondary">{accommodation.address}</Typography>
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="body2">
                                  <strong>Check-in:</strong> {format(accommodation.checkIn, 'MMM d, yyyy h:mm a')}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Check-out:</strong> {format(accommodation.checkOut, 'MMM d, yyyy h:mm a')}
                                </Typography>
                                {accommodation.price !== undefined && (
                                  <Typography variant="body2">
                                    <strong>Price:</strong> ${accommodation.price.toFixed(2)}
                                  </Typography>
                                )}
                                {accommodation.notes && (
                                  <Typography variant="body2">
                                    <strong>Notes:</strong> {accommodation.notes}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box>
                              <IconButton size="small" onClick={() => handleEditAccommodation(accommodation, index)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleRemoveAccommodation(index)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : selectedTrip ? 'Update Trip' : 'Create Trip'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Accommodation Form Dialog */}
      <Dialog
        open={accommodationFormOpen}
        onClose={() => setAccommodationFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {currentAccommodation.id ? 'Edit Accommodation' : 'Add Accommodation'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Accommodation Name"
                fullWidth
                required
                value={currentAccommodation.name}
                onChange={handleAccommodationInputChange}
                error={accommodationErrors.name}
                helperText={accommodationErrors.name ? 'Name is required' : ''}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="address"
                label="Address"
                fullWidth
                required
                value={currentAccommodation.address}
                onChange={handleAccommodationInputChange}
                error={accommodationErrors.address}
                helperText={accommodationErrors.address ? 'Address is required' : ''}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button 
                size="small"
                variant="outlined"
                startIcon={<EventAvailableIcon />}
                onClick={() => {
                  // Inherit trip dates
                  setCurrentAccommodation({
                    ...currentAccommodation,
                    checkIn: formData.startDate,
                    checkOut: formData.endDate
                  });
                  
                  // Clear any errors related to dates
                  setAccommodationErrors({
                    ...accommodationErrors,
                    checkIn: false,
                    checkOut: false
                  });
                }}
                sx={{ mb: 2 }}
              >
                Use Trip Dates
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Check-in Date & Time"
                  value={currentAccommodation.checkIn}
                  onChange={(date) => handleAccommodationDateChange('checkIn', date)}
                 
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth required error={accommodationErrors.checkIn} helperText={accommodationErrors.checkIn ? 'Check-in date is required' : ''} />
                  )}
                  ampm={true}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Check-out Date & Time"
                  value={currentAccommodation.checkOut}
                  onChange={(date) => handleAccommodationDateChange('checkOut', date)}
                 
                  renderInput={params => (
                    <TextField {...params} size='small' fullWidth required error={accommodationErrors.checkOut} helperText={accommodationErrors.checkOut ? 'Check-out date is required' : ''} />
                  )}
                  ampm={true}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="price"
                label="Price (Optional)"
                fullWidth
                type="number"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                value={currentAccommodation.price}
                onChange={handleAccommodationInputChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                fullWidth
                multiline
                rows={3}
                value={currentAccommodation.notes}
                onChange={handleAccommodationInputChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccommodationFormOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleSaveAccommodation} 
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Trip Dialog */}
      <Dialog
        open={deleteTripDialogOpen}
        onClose={handleDialogClose}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this trip? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteTrip} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrganizationTripsPage;